import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  LinearProgress,
} from '@mui/material';
import {
  Plus,
  Edit,
  DollarSign,
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  Users,
  TrendingUp,
  AlertTriangle,
  BarChart3,
  XCircle,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { paymentsApi, studentsApi, schoolFeesApi, classroomsApi, Payment, Student, SchoolFee, Classroom } from '../../services/schoolApi';
import { printSchoolPaymentReceipt, isBluetoothAvailable, SchoolPaymentReceiptData } from '../../services/thermalPrinterService';

const Payments = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  
  const [payments, setPayments] = useState<Payment[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [fees, setFees] = useState<SchoolFee[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMethod, setFilterMethod] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  
  // Statistics
  const [statistics, setStatistics] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // Unpaid students
  const [unpaidData, setUnpaidData] = useState<any>(null);
  const [unpaidLoading, setUnpaidLoading] = useState(false);
  const [selectedFeeForUnpaid, setSelectedFeeForUnpaid] = useState('');
  const [selectedClassroomForUnpaid, setSelectedClassroomForUnpaid] = useState('');
  
  // Student payment history
  const [selectedStudentForHistory, setSelectedStudentForHistory] = useState('');
  const [studentHistory, setStudentHistory] = useState<any>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    studentId: '',
    schoolFeeId: '',
    amount: '',
    paymentMethod: 'cash' as 'cash' | 'mobile_money' | 'bank_transfer' | 'check',
    paymentDate: '',
    reference: '',
    notes: '',
  });

  const text = {
    fr: {
      title: 'Gestion des Paiements',
      description: 'Enregistrer et suivre les paiements des élèves',
      addPayment: 'Enregistrer un paiement',
      student: 'Élève',
      schoolFee: 'Frais scolaire',
      amount: 'Montant',
      paymentMethod: 'Méthode de paiement',
      paymentDate: 'Date de paiement',
      reference: 'Référence',
      notes: 'Notes',
      status: 'Statut',
      actions: 'Actions',
      edit: 'Modifier',
      cancel: 'Annuler',
      save: 'Enregistrer',
      editPayment: 'Modifier le paiement',
      noPayments: 'Aucun paiement trouvé',
      loading: 'Chargement...',
      filterStatus: 'Filtrer par statut',
      filterMethod: 'Filtrer par méthode',
      all: 'Tous',
      cash: 'Espèces',
      mobile_money: 'Mobile Money',
      bank_transfer: 'Virement bancaire',
      check: 'Chèque',
      completed: 'Complété',
      partial: 'Partiel',
      pending: 'En attente',
      tabRecent: 'Paiements récents',
      tabStatistics: 'Statistiques',
      tabUnpaid: 'Impayés',
      tabHistory: 'Historique élève',
      selectFee: 'Sélectionner un frais',
      selectClassroom: 'Filtrer par classe',
      selectStudent: 'Sélectionner un élève',
      search: 'Rechercher',
      totalCollected: 'Total collecté',
      totalExpected: 'Total attendu',
      collectionRate: 'Taux de recouvrement',
      totalPayments: 'Total paiements',
      unpaidStudents: 'Élèves impayés',
      remaining: 'Restant',
      progress: 'Progression',
      paymentCount: 'Nb paiements',
      totalPaid: 'Total payé',
      noData: 'Aucune donnée disponible',
      cancelPayment: 'Annuler le paiement',
      saving: 'Enregistrement...',
      printing: 'Impression...',
      printError: 'Paiement enregistré mais erreur impression',
      saveAndPrint: 'Enregistrer & Imprimer',
    },
    en: {
      title: 'Payments Management',
      description: 'Record and track student payments',
      addPayment: 'Record Payment',
      student: 'Student',
      schoolFee: 'School Fee',
      amount: 'Amount',
      paymentMethod: 'Payment Method',
      paymentDate: 'Payment Date',
      reference: 'Reference',
      notes: 'Notes',
      status: 'Status',
      actions: 'Actions',
      edit: 'Edit',
      cancel: 'Cancel',
      save: 'Save',
      editPayment: 'Edit Payment',
      noPayments: 'No payments found',
      loading: 'Loading...',
      filterStatus: 'Filter by status',
      filterMethod: 'Filter by method',
      all: 'All',
      cash: 'Cash',
      mobile_money: 'Mobile Money',
      bank_transfer: 'Bank Transfer',
      check: 'Check',
      completed: 'Completed',
      partial: 'Partial',
      pending: 'Pending',
      tabRecent: 'Recent Payments',
      tabStatistics: 'Statistics',
      tabUnpaid: 'Unpaid',
      tabHistory: 'Student History',
      selectFee: 'Select a fee',
      selectClassroom: 'Filter by class',
      selectStudent: 'Select a student',
      search: 'Search',
      totalCollected: 'Total collected',
      totalExpected: 'Total expected',
      collectionRate: 'Collection rate',
      totalPayments: 'Total payments',
      unpaidStudents: 'Unpaid students',
      remaining: 'Remaining',
      progress: 'Progress',
      paymentCount: 'Payment count',
      totalPaid: 'Total paid',
      noData: 'No data available',
      cancelPayment: 'Cancel payment',
      saving: 'Saving...',
      printing: 'Printing...',
      printError: 'Payment saved but print failed',
      saveAndPrint: 'Save & Print',
    }
  };

  const currentText = text[language as keyof typeof text];

  const paymentMethods = [
    { value: 'cash', label: currentText.cash },
    { value: 'mobile_money', label: currentText.mobile_money },
    { value: 'bank_transfer', label: currentText.bank_transfer },
    { value: 'check', label: currentText.check },
  ];

  const statuses = [
    { value: 'completed', label: currentText.completed },
    { value: 'partial', label: currentText.partial },
    { value: 'pending', label: currentText.pending },
  ];

  useEffect(() => {
    fetchStudents();
    fetchFees();
    fetchClassrooms();
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [page, filterStatus, filterMethod]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await paymentsApi.getRecent({
        page,
        limit: 10,
      });
      
      let filteredPayments = response.payments || [];
      if (filterStatus) {
        filteredPayments = filteredPayments.filter(p => p.status === filterStatus);
      }
      if (filterMethod) {
        filteredPayments = filteredPayments.filter(p => p.paymentMethod === filterMethod);
      }
      
      setPayments(filteredPayments);
      setTotalPages(response.pagination?.pages || 1);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des paiements');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await studentsApi.getAll({ limit: 1000 });
      setStudents(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des élèves:', err);
    }
  };

  const fetchClassrooms = async () => {
    try {
      const response = await classroomsApi.getAll({ limit: 100 });
      setClassrooms(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des classes:', err);
    }
  };

  const fetchFees = async () => {
    try {
      const response = await schoolFeesApi.getAll({ limit: 100 });
      setFees(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des frais:', err);
    }
  };

  const fetchStatistics = async () => {
    try {
      setStatsLoading(true);
      const raw = await paymentsApi.getStatistics({ period: 'current' });
      console.log('📊 Statistics RAW response:', JSON.stringify(raw, null, 2));
      
      // Auto-detect structure: API may return:
      // 1) { statistics: { monthlyPayments, paymentRate, ... } }
      // 2) { monthlyPayments, paymentRate, ... }  
      // 3) { data: { statistics: ... } }
      // 4) { data: { monthlyPayments, ... } }
      // We normalize to always have stats.monthlyPayments etc.
      let stats: any = null;
      if (raw?.statistics?.monthlyPayments || raw?.statistics?.paymentRate || raw?.statistics?.schoolFeesBreakdown) {
        stats = raw.statistics;
      } else if (raw?.data?.statistics) {
        stats = raw.data.statistics;
      } else if (raw?.data?.monthlyPayments || raw?.data?.paymentRate) {
        stats = raw.data;
      } else if (raw?.monthlyPayments || raw?.paymentRate || raw?.schoolFeesBreakdown) {
        stats = raw;
      } else {
        // Last resort: maybe it's wrapped in 'success' pattern
        stats = raw?.data || raw;
      }
      
      console.log('📊 Statistics NORMALIZED:', JSON.stringify(stats, null, 2));
      setStatistics(stats);
    } catch (err: any) {
      console.error('📊 Statistics ERROR:', err);
      setError(err.message || 'Erreur lors du chargement des statistiques');
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchUnpaid = async () => {
    if (!selectedFeeForUnpaid) return;
    try {
      setUnpaidLoading(true);
      const data = await paymentsApi.getUnpaid(selectedFeeForUnpaid, {
        classroomId: selectedClassroomForUnpaid || undefined,
        limit: 50,
      });
      setUnpaidData(data);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des impayés');
    } finally {
      setUnpaidLoading(false);
    }
  };

  const fetchStudentHistory = async () => {
    if (!selectedStudentForHistory) return;
    try {
      setHistoryLoading(true);
      const data = await paymentsApi.getByStudent(selectedStudentForHistory, { limit: 50 });
      setStudentHistory(data);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement de l\'historique');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCancelPayment = async (paymentId: string) => {
    try {
      await paymentsApi.cancel(paymentId);
      fetchPayments();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'annulation');
    }
  };

  useEffect(() => {
    if (activeTab === 1) fetchStatistics();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 2 && selectedFeeForUnpaid) fetchUnpaid();
  }, [selectedFeeForUnpaid, selectedClassroomForUnpaid]);

  useEffect(() => {
    if (activeTab === 3 && selectedStudentForHistory) fetchStudentHistory();
  }, [selectedStudentForHistory]);

  const handleOpenDialog = (payment?: Payment) => {
    if (payment) {
      setEditingPayment(payment);
      setFormData({
        studentId: typeof payment.studentId === 'string' ? payment.studentId : payment.studentId._id,
        schoolFeeId: typeof payment.schoolFeeId === 'string' ? payment.schoolFeeId : payment.schoolFeeId._id,
        amount: payment.amount.toString(),
        paymentMethod: payment.paymentMethod,
        paymentDate: payment.paymentDate ? payment.paymentDate.split('T')[0] : '',
        reference: payment.reference || '',
        notes: payment.notes || '',
      });
    } else {
      setEditingPayment(null);
      setFormData({
        studentId: '',
        schoolFeeId: '',
        amount: '',
        paymentMethod: 'cash',
        paymentDate: new Date().toISOString().split('T')[0],
        reference: '',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPayment(null);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
      };
      
      if (editingPayment) {
        await paymentsApi.update(editingPayment._id, data);
      } else {
        await paymentsApi.create(data);
      }

      // Tenter l'impression thermique
      if (isBluetoothAvailable()) {
        try {
          const student = students.find(s => s._id === formData.studentId);
          const fee = fees.find(f => f._id === formData.schoolFeeId);
          const methodLabels: Record<string, string> = {
            cash: currentText.cash,
            mobile_money: currentText.mobile_money,
            bank_transfer: currentText.bank_transfer,
            check: currentText.check,
          };

          // Retrouver le nom de la classe
          let className = '-';
          if (student) {
            if (typeof student.classroomId === 'object' && student.classroomId?.name) {
              className = student.classroomId.name;
            } else {
              const cls = classrooms.find(c => c._id === (typeof student.classroomId === 'string' ? student.classroomId : student.classroomId?._id));
              if (cls) className = cls.name;
            }
          }

          const receiptData: SchoolPaymentReceiptData = {
            schoolName: user?.company?.name || 'Ecole',
            studentName: student ? `${student.firstName} ${student.lastName}` : '-',
            studentMatricule: student?.matricule || '-',
            className,
            feeLabel: fee?.label || '-',
            amount: parseFloat(formData.amount),
            currency: fee?.currency || user?.company?.signCurrency || 'USD',
            paymentMethod: methodLabels[formData.paymentMethod] || formData.paymentMethod,
            reference: formData.reference || undefined,
            notes: formData.notes || undefined,
            date: formData.paymentDate ? new Date(formData.paymentDate) : new Date(),
          };

          await printSchoolPaymentReceipt(receiptData);
        } catch (printErr: any) {
          console.warn('Print error:', printErr);
          setError(currentText.printError);
        }
      }

      handleCloseDialog();
      fetchPayments();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const getStudentName = (studentId: string | Student | undefined) => {
    if (!studentId) return '-';
    if (typeof studentId === 'object' && studentId !== null) {
      return `${studentId.firstName || ''} ${studentId.lastName || ''}`.trim() || '-';
    }
    const student = students.find(s => s._id === studentId);
    return student ? `${student.firstName} ${student.lastName}` : '-';
  };

  const getFeeLabel = (schoolFeeId: string | SchoolFee | undefined) => {
    if (!schoolFeeId) return '-';
    if (typeof schoolFeeId === 'object' && schoolFeeId !== null) {
      return schoolFeeId.label || '-';
    }
    const fee = fees.find(f => f._id === schoolFeeId);
    return fee?.label || '-';
  };

  const getMethodLabel = (method: string) => {
    const paymentMethod = paymentMethods.find(m => m.value === method);
    return paymentMethod?.label || method;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} />;
      case 'partial':
        return <Clock size={16} />;
      case 'pending':
        return <Clock size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'partial':
        return 'warning';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 1 }}>
            {currentText.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {currentText.description}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={20} />}
          onClick={() => handleOpenDialog()}
          sx={{ 
            backgroundColor: '#0D3C4C',
            '&:hover': { backgroundColor: '#1A5F7A' }
          }}
        >
          {currentText.addPayment}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<CreditCard size={18} />} iconPosition="start" label={currentText.tabRecent} />
          <Tab icon={<BarChart3 size={18} />} iconPosition="start" label={currentText.tabStatistics} />
          <Tab icon={<AlertTriangle size={18} />} iconPosition="start" label={currentText.tabUnpaid} />
          <Tab icon={<Users size={18} />} iconPosition="start" label={currentText.tabHistory} />
        </Tabs>
      </Paper>

      {/* TAB 0: Recent Payments */}
      {activeTab === 0 && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>{currentText.filterStatus}</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label={currentText.filterStatus}
                >
                  <MenuItem value="">{currentText.all}</MenuItem>
                  {statuses.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>{currentText.filterMethod}</InputLabel>
                <Select
                  value={filterMethod}
                  onChange={(e) => setFilterMethod(e.target.value)}
                  label={currentText.filterMethod}
                >
                  <MenuItem value="">{currentText.all}</MenuItem>
                  {paymentMethods.map((method) => (
                    <MenuItem key={method.value} value={method.value}>
                      {method.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.reference}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.student}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.schoolFee}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.amount}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.paymentMethod}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.paymentDate}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.status}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                      <Typography sx={{ mt: 2 }}>{currentText.loading}</Typography>
                    </TableCell>
                  </TableRow>
                ) : payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">{currentText.noPayments}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment._id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{payment.reference || '-'}</TableCell>
                      <TableCell>{getStudentName(payment.studentId)}</TableCell>
                      <TableCell>{getFeeLabel(payment.schoolFeeId)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <DollarSign size={16} />
                          {payment.amount.toLocaleString()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={getMethodLabel(payment.paymentMethod)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {payment.paymentDate ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Calendar size={16} />
                            {new Date(payment.paymentDate).toLocaleDateString()}
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(payment.status)}
                          label={statuses.find(s => s.value === payment.status)?.label || payment.status}
                          color={getStatusColor(payment.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(payment)}
                          sx={{ color: '#0D3C4C' }}
                        >
                          <Edit size={18} />
                        </IconButton>
                        {(payment.status as string) !== 'cancelled' && (
                          <IconButton
                            size="small"
                            onClick={() => handleCancelPayment(payment._id)}
                            sx={{ color: '#d32f2f' }}
                            title={currentText.cancelPayment}
                          >
                            <XCircle size={18} />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* TAB 1: Statistics */}
      {activeTab === 1 && (
        <Box>
          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : statistics ? (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: '#0D3C4C' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <DollarSign size={24} color="white" />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        {currentText.totalCollected}
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                      {(statistics?.monthlyPayments?.totalAmount || 0).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: '#1A5F7A' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TrendingUp size={24} color="white" />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        {currentText.collectionRate}
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                      {statistics?.paymentRate?.global || 0}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: '#2E8B9E' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Users size={24} color="white" />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        {currentText.totalPayments}
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                      {statistics?.monthlyPayments?.count || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: '#159895' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CheckCircle size={24} color="white" />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        {currentText.totalExpected}
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                      {(statistics?.paymentRate?.totalRequiredAmount || 0).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Payment methods breakdown */}
              {statistics?.paymentMethods && (
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 2 }}>
                        {currentText.paymentMethod}
                      </Typography>
                      {Object.entries(statistics.paymentMethods).map(([method, count]) => (
                        <Box key={method} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">{getMethodLabel(method)}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{count as number}</Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* School fees breakdown */}
              {(statistics?.schoolFeesBreakdown || statistics?.paymentRate?.bySchoolFee) && (
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 2 }}>
                        {currentText.schoolFee}
                      </Typography>
                      {(statistics.schoolFeesBreakdown || statistics.paymentRate?.bySchoolFee || []).map((fee: any) => {
                        const label = fee.label || fee.schoolFeeName || fee.name || fee.schoolFeeId || '-';
                        const rate = parseFloat(String(fee.paymentRate ?? fee.rate ?? fee.percentage ?? 0));
                        return (
                          <Box key={fee.schoolFeeId || fee._id || label} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2">{label}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {rate}%
                                {fee.totalPaid != null && fee.totalRequired != null && (
                                  <> ({Number(fee.totalPaid).toLocaleString()} / {Number(fee.totalRequired).toLocaleString()})</>
                                )}
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(rate, 100)} 
                              sx={{ 
                                height: 8, 
                                borderRadius: 4,
                                backgroundColor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 4,
                                  backgroundColor: rate >= 75 ? '#4caf50' : rate >= 50 ? '#ff9800' : '#f44336',
                                },
                              }}
                            />
                          </Box>
                        );
                      })}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{currentText.noData}</Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* TAB 2: Unpaid students */}
      {activeTab === 2 && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 250 }}>
                <InputLabel>{currentText.selectFee}</InputLabel>
                <Select
                  value={selectedFeeForUnpaid}
                  onChange={(e) => setSelectedFeeForUnpaid(e.target.value)}
                  label={currentText.selectFee}
                >
                  {fees.map((fee) => (
                    <MenuItem key={fee._id} value={fee._id}>
                      {fee.label} - {fee.amount.toLocaleString()} {fee.currency || 'CDF'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>{currentText.selectClassroom}</InputLabel>
                <Select
                  value={selectedClassroomForUnpaid}
                  onChange={(e) => setSelectedClassroomForUnpaid(e.target.value)}
                  label={currentText.selectClassroom}
                >
                  <MenuItem value="">{currentText.all}</MenuItem>
                  {classrooms.map((c) => (
                    <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {unpaidLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : unpaidData ? (
            <>
              {unpaidData.summary && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                          {unpaidData.summary.unpaidStudents || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {currentText.unpaidStudents}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                          {unpaidData.summary.fullyPaidStudents || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {currentText.completed}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D3C4C' }}>
                          {(unpaidData.summary.totalUnpaidAmount || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {currentText.remaining}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>{currentText.student}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{currentText.totalPaid}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{currentText.remaining}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{currentText.progress}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{currentText.status}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(unpaidData.students || []).map((s: any) => (
                      <TableRow key={s._id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {s.firstName} {s.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {s.matricule}
                          </Typography>
                        </TableCell>
                        <TableCell>{(s.paymentInfo?.totalPaid || 0).toLocaleString()}</TableCell>
                        <TableCell sx={{ color: '#d32f2f', fontWeight: 600 }}>
                          {(s.paymentInfo?.remainingAmount || 0).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={s.paymentInfo?.progressPercentage || 0}
                              sx={{ flex: 1, height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="caption">
                              {s.paymentInfo?.progressPercentage || 0}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={s.paymentInfo?.paymentStatus || 'unpaid'}
                            color={s.paymentInfo?.paymentStatus === 'partial' ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {selectedFeeForUnpaid ? currentText.noData : currentText.selectFee}
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* TAB 3: Student History */}
      {activeTab === 3 && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>{currentText.selectStudent}</InputLabel>
              <Select
                value={selectedStudentForHistory}
                onChange={(e) => setSelectedStudentForHistory(e.target.value)}
                label={currentText.selectStudent}
              >
                {students.map((s) => (
                  <MenuItem key={s._id} value={s._id}>
                    {s.firstName} {s.lastName} - {s.matricule}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : studentHistory ? (
            <>
              {studentHistory.student && (
                <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {studentHistory.student.firstName} {studentHistory.student.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {studentHistory.student.matricule} - {studentHistory.student.classroomId?.name || ''}
                  </Typography>
                  {studentHistory.summary && (
                    <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                      <Typography variant="body2">
                        {currentText.totalPaid}: <strong>{(studentHistory.summary.totalPaid || 0).toLocaleString()}</strong>
                      </Typography>
                      <Typography variant="body2">
                        {currentText.paymentCount}: <strong>{studentHistory.summary.paymentCount || 0}</strong>
                      </Typography>
                    </Box>
                  )}
                </Paper>
              )}

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>{currentText.schoolFee}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{currentText.amount}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{currentText.paymentDate}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{currentText.paymentMethod}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{currentText.status}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(studentHistory.payments || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">{currentText.noPayments}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (studentHistory.payments || []).map((p: any) => (
                        <TableRow key={p._id} hover>
                          <TableCell>{p.schoolFeeId?.label || '-'}</TableCell>
                          <TableCell>{(p.amount || 0).toLocaleString()}</TableCell>
                          <TableCell>
                            {p.paymentDate ? new Date(p.paymentDate).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip label={getMethodLabel(p.paymentMethod)} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={statuses.find(s => s.value === p.status)?.label || p.status}
                              color={getStatusColor(p.status) as any}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{currentText.selectStudent}</Typography>
            </Paper>
          )}
        </Box>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPayment ? currentText.editPayment : currentText.addPayment}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>{currentText.student}</InputLabel>
              <Select
                value={formData.studentId}
                onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}
                label={currentText.student}
              >
                {students.map((student) => (
                  <MenuItem key={student._id} value={student._id}>
                    {student.firstName} {student.lastName} - {student.matricule}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>{currentText.schoolFee}</InputLabel>
              <Select
                value={formData.schoolFeeId}
                onChange={(e) => setFormData({ ...formData, schoolFeeId: e.target.value })}
                label={currentText.schoolFee}
              >
                {fees.map((fee) => (
                  <MenuItem key={fee._id} value={fee._id}>
                    {fee.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={currentText.amount}
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>{currentText.paymentMethod}</InputLabel>
              <Select
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value as any })}
                label={currentText.paymentMethod}
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={currentText.paymentDate}
              type="date"
              value={formData.paymentDate}
              onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={currentText.reference}
              value={formData.reference}
              onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
              fullWidth
            />
            <TextField
              label={currentText.notes}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              sx={{ gridColumn: '1 / -1' }}
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>{currentText.cancel}</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ 
              backgroundColor: '#0D3C4C',
              '&:hover': { backgroundColor: '#1A5F7A' }
            }}
          >
            {submitting ? currentText.saving : currentText.saveAndPrint}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Payments;
